import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Reveal from "@/components/base/Reveal";

export default function AboutPage() {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = "About L+TV | L+TV TÉLÉVISION";
  }, []);

  const values = [
    { name: t("about.value.excellence"), description: t("about.value.excellenceDesc") },
    { name: t("about.value.professionalism"), description: t("about.value.professionalismDesc") },
    { name: t("about.value.discipline"), description: t("about.value.disciplineDesc") },
    { name: t("about.value.integrity"), description: t("about.value.integrityDesc") },
    { name: t("about.value.community"), description: t("about.value.communityDesc") },
    { name: t("about.value.creativity"), description: t("about.value.creativityDesc") },
    { name: t("about.value.responsible"), description: t("about.value.responsibleDesc") },
  ];

  const departments = [
    { name: t("about.dept.news"), description: t("about.dept.newsDesc"), path: "/news" },
    { name: t("about.dept.entertainment"), description: t("about.dept.entertainmentDesc"), path: "/entertainment" },
    { name: t("about.dept.technical"), description: t("about.dept.technicalDesc"), path: "/live" },
    { name: t("about.dept.photo"), description: t("about.dept.photoDesc"), path: "/photo-studio" },
    { name: t("about.dept.production"), description: t("about.dept.productionDesc"), path: "/production" },
  ];

  return (
    <div className="pt-20">
      <div className="bg-primary-900 py-8">
        <div className="ltv-container">
          <Reveal direction="up">
            <h1 className="text-2xl md:text-3xl font-bold text-white">{t("about.title")}</h1>
            <p className="text-white/60 text-sm mt-1">{t("about.subtitle")}</p>
          </Reveal>
        </div>
      </div>

      <div className="ltv-container py-8 md:py-12">
        {/* Who We Are */}
        <section className="mb-16">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <Reveal direction="right">
              <div>
                <h2 className="ltv-section-title">{t("about.whoWeAre")}</h2>
                <p className="text-foreground-700 leading-relaxed mb-4">{t("about.whoP1")}</p>
                <p className="text-foreground-700 leading-relaxed mb-4">{t("about.whoP2")}</p>
                <p className="text-foreground-700 leading-relaxed">{t("about.whoP3")}</p>
              </div>
            </Reveal>
            <Reveal direction="left">
              <div className="rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20television%20studio%20broadcast%20room%20with%20modern%20equipment%20and%20warm%20lighting%2C%20clean%20editorial%20photography%20style%2C%20African%20broadcast%20station%20interior&width=800&height=600&seq=about-1&orientation=landscape"
                  alt="L+TV Studio"
                  className="w-full aspect-[4/3] object-cover"
                />
              </div>
            </Reveal>
          </div>
        </section>

        {/* Vision */}
        <Reveal direction="up">
          <section className="mb-16 bg-primary-50 rounded-lg border border-primary-100 p-8 md:p-12">
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-primary-900 mb-4">{t("about.ourVision")}</h2>
              <p className="text-lg md:text-xl text-primary-800 font-medium leading-relaxed">
                "{t("about.visionQuote")}"
              </p>
            </div>
          </section>
        </Reveal>

        {/* Mission */}
        <Reveal direction="up">
          <section className="mb-16">
            <h2 className="ltv-section-title">{t("about.ourMission")}</h2>
            <p className="text-foreground-700 leading-relaxed max-w-3xl">{t("about.missionText")}</p>
          </section>
        </Reveal>

        {/* Values */}
        <section className="mb-16">
          <h2 className="ltv-section-title">{t("about.ourValues")}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {values.map((v, index) => (
              <Reveal key={v.name} direction="up" delay={(index % 3) * 80}>
                <div className="bg-white rounded-lg border border-background-200 p-5 h-full">
                  <h3 className="font-semibold text-foreground-900 mb-2">{v.name}</h3>
                  <p className="text-sm text-foreground-600">{v.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* Departments */}
        <section className="mb-16">
          <h2 className="ltv-section-title">{t("about.ourDepartments")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {departments.map((d, index) => (
              <Reveal key={d.name} direction="up" delay={(index % 2) * 80}>
                <Link to={d.path} className="bg-white rounded-lg border border-background-200 p-5 hover:border-accent-200 transition-colors group block h-full">
                  <h3 className="font-semibold text-foreground-900 mb-2 group-hover:text-accent-600 transition-colors">{d.name}</h3>
                  <p className="text-sm text-foreground-600">{d.description}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}